import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Menu,
	MenuItem,
	Popover,
	Stack,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";

import DeleteOutline from "@mui/icons-material/DeleteOutline";

import {
	atualizarAba,
	carregarAbas,
	criarAba,
	excluirAba,
	getSnapshot,
	subscribe,
} from "./abasCache";
import stylesFn from "./styles";

// assina o catálogo de abas; o cache garante 1 requisição para várias telas
export function useAbasCatalogo(notify) {
	const [snapshot, setSnapshot] = useState(getSnapshot);

	useEffect(() => {
		const cancelar = subscribe(() => setSnapshot(getSnapshot()));
		carregarAbas().catch(() => {
			notify?.({
				children: "Error: Não foi possível carregar as abas do schema",
				severity: "error",
			});
		});
		return cancelar;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		abas: snapshot.abas || [],
		carregando: snapshot.carregando,
	};
}

// lista as abas do catálogo ainda não usadas e permite cadastrar uma nova
export function AbaAddButton({
	disponiveis,
	onAdicionar,
	notify,
	sx,
	children = "Adicionar aba",
}) {
	const theme = useTheme();
	const styles = useMemo(() => stylesFn(theme), [theme]);

	const [menu, setMenu] = useState(null);
	const [criando, setCriando] = useState(false);

	return (
		<>
			<Button
				size="small"
				onClick={(event) => setMenu(event.currentTarget)}
				sx={{ ...styles.botaoTracejado, ...sx }}
				fullWidth
			>
				+ {children}
			</Button>

			<Menu open={Boolean(menu)} anchorEl={menu} onClose={() => setMenu(null)}>
				{disponiveis.length === 0 && (
					<MenuItem disabled>
						<Typography variant="caption">
							Todas as abas do catálogo já estão neste schema
						</Typography>
					</MenuItem>
				)}

				{disponiveis.map((aba) => (
					<MenuItem
						key={aba.id || aba.key}
						onClick={() => {
							setMenu(null);
							onAdicionar(aba.key);
						}}
					>
						<Stack>
							<Typography variant="body2">{aba.label}</Typography>
							<Typography sx={styles.chaveMono}>{aba.key}</Typography>
						</Stack>
					</MenuItem>
				))}

				<Divider />
				<MenuItem
					onClick={() => {
						setMenu(null);
						setCriando(true);
					}}
				>
					<Typography variant="body2" color="primary">
						Cadastrar nova aba…
					</Typography>
				</MenuItem>
			</Menu>

			<AbaCreateDialog
				open={criando}
				onClose={() => setCriando(false)}
				notify={notify}
				onCriada={(aba) => {
					setCriando(false);
					onAdicionar(aba.key);
				}}
			/>
		</>
	);
}

// key e label separados: key precisa bater com o app do cliente, label é livre
function AbaCreateDialog({ open, onClose, onCriada, notify }) {
	const theme = useTheme();
	const styles = useMemo(() => stylesFn(theme), [theme]);

	const [key, setKey] = useState("");
	const [label, setLabel] = useState("");
	const [ocupado, setOcupado] = useState(false);

	useEffect(() => {
		if (open) {
			setKey("");
			setLabel("");
		}
	}, [open]);

	const salvar = async () => {
		setOcupado(true);
		try {
			const resultado = await criarAba({ key: key.trim(), label: label.trim() });
			if (!resultado?.aba) return;

			if (!resultado.created) {
				notify?.({
					children: `A aba "${resultado.aba.key}" já existia e foi reaproveitada`,
					severity: "info",
				});
			}
			onCriada(resultado.aba);
		} catch (e) {
			notify?.({
				children:
					e?.response?.data?.error || "Error: Não foi possível criar a aba",
				severity: "error",
			});
		} finally {
			setOcupado(false);
		}
	};

	return (
		<Dialog open={open} onClose={ocupado ? undefined : onClose} maxWidth="xs" fullWidth>
			<DialogTitle>Nova aba do schema</DialogTitle>
			<DialogContent dividers>
				<Stack gap={2}>
					<Alert severity="info" sx={styles.alertaInfo}>
						A <strong>chave</strong> precisa ser exatamente a que o app do
						cliente espera — é por ela que ele identifica a tela. O{" "}
						<strong>rótulo</strong> é livre e aparece só aqui.
					</Alert>

					<TextField
						variant="standard"
						label="Chave (usada no JSON)"
						placeholder="linkSession"
						value={key}
						onChange={(e) => setKey(e.target.value)}
						disabled={ocupado}
						fullWidth
						InputProps={{ sx: styles.inputMono }}
					/>
					<TextField
						variant="standard"
						label="Rótulo"
						placeholder="Links e sessões"
						value={label}
						onChange={(e) => setLabel(e.target.value)}
						disabled={ocupado}
						fullWidth
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={ocupado}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={salvar}
					disabled={ocupado || !label.trim() || !key.trim()}
				>
					{ocupado ? <CircularProgress size={20} /> : "Cadastrar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// a chave não aparece pra edição: o backend só permite trocá-la com uso zero
export function AbaEditPopover({ estado, onClose, notify, onExcluida }) {
	const theme = useTheme();
	const styles = useMemo(() => stylesFn(theme), [theme]);

	const aba = estado?.aba;
	const [label, setLabel] = useState("");
	const [ocupado, setOcupado] = useState(false);
	const [conflito, setConflito] = useState(null);

	useEffect(() => {
		setLabel(aba?.label || "");
		setConflito(null);
	}, [aba]);

	if (!aba) return null;

	const salvar = async () => {
		setOcupado(true);
		try {
			await atualizarAba({ id: aba.id, label });
			notify?.({ children: "Aba atualizada", severity: "success" });
			onClose();
		} catch (e) {
			notify?.({
				children:
					e?.response?.data?.error || "Error: Não foi possível atualizar a aba",
				severity: "error",
			});
		} finally {
			setOcupado(false);
		}
	};

	const excluir = async (force) => {
		setOcupado(true);
		try {
			await excluirAba({ id: aba.id, force });
			notify?.({ children: "Aba removida do catálogo", severity: "success" });
			onExcluida?.(aba.key);
			onClose();
		} catch (e) {
			if (e?.response?.status === 409) setConflito(e.response.data);
			else {
				notify?.({
					children:
						e?.response?.data?.error || "Error: Não foi possível excluir a aba",
					severity: "error",
				});
			}
		} finally {
			setOcupado(false);
		}
	};

	return (
		<Popover
			open
			anchorEl={estado.anchorEl}
			onClose={onClose}
			anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			slotProps={{ paper: { sx: styles.editPaper } }}
		>
			<Typography sx={styles.chaveMonoDestaque}>{aba.key}</Typography>
			<Typography variant="caption" color="text.secondary">
				A chave é fixa: o app do cliente identifica a tela por ela.
			</Typography>

			<TextField
				variant="standard"
				label="Rótulo"
				value={label}
				onChange={(e) => setLabel(e.target.value)}
				disabled={ocupado}
				fullWidth
			/>

			{conflito && (
				<Alert severity="warning" sx={styles.alertaInfo}>
					{conflito.error}
					<Box component="ul" sx={styles.listaModelos}>
						{(conflito.modelos || []).slice(0, 6).map((m) => (
							<li key={m.modeloId}>
								{[m.fabricante, m.funcao, m.modelo].filter(Boolean).join(" · ")}
							</li>
						))}
						{(conflito.modelos || []).length > 6 && (
							<li>…e mais {conflito.modelos.length - 6}</li>
						)}
					</Box>
					<Typography variant="caption" display="block">
						Remover do catálogo não altera nenhum dashboard salvo — a aba só
						deixa de ser oferecida aqui.
					</Typography>
					<Button
						size="small"
						color="warning"
						disabled={ocupado}
						onClick={() => excluir(true)}
					>
						Remover do catálogo mesmo assim
					</Button>
				</Alert>
			)}

			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Button
					size="small"
					color="error"
					disabled={ocupado}
					onClick={() => excluir(false)}
					startIcon={<DeleteOutline fontSize="small" />}
				>
					Excluir do catálogo
				</Button>
				<Stack direction="row" gap={1}>
					<Button size="small" onClick={onClose} disabled={ocupado}>
						Cancelar
					</Button>
					<Button
						size="small"
						variant="contained"
						onClick={salvar}
						disabled={ocupado || !label.trim()}
					>
						{ocupado ? <CircularProgress size={16} /> : "Salvar"}
					</Button>
				</Stack>
			</Stack>
		</Popover>
	);
}
